import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRightOutlined, PlusOutlined } from '@ant-design/icons';
import { Check, X, MessageSquare, Video, Phone, PhoneOutgoing, User } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../../utils/persistentNavigation';
import { SEO_DEFAULTS } from '../../../utils/seoConfig';
import { generateFAQSchema, generateBreadcrumbSchema, JsonLd } from '../../../utils/structuredData';
import { trackEvent } from '../../../utils/analytics';
import { useInMarketingShell } from '../../Marketing/MarketingContext';
import WallFrame from '../sections/WallFrame';
import AIEClose from '../sections/AIEClose';
import { MEETINGS } from './meetingsContent';
import '../AIEmployeesLandingPage.css';
import '../sections/AIEStepsStrip.css';
import '../sections/AIEFaq.css';
import './MeetingsLandingPage.css';

/**
 * MeetingsLandingPage: /ai-employees/meetings (Nitish-ruled Sep 29 2026, Maeve's session;
 * mock v5 in Maeve's work/meetings_page/). The link destination for the meeting films,
 * clips, desk replies and lifecycle emails (Rhea: not a keyword play).
 *
 * Content, JSON-LD and the .md alternate all come from meetingsContent.js (generated).
 * Reuses the REAL wall Meet frame (WallFrame num "09", same component /super-agents uses)
 * and the real AIEClose. Everything else is page-local under the .mtg root.
 *
 * Films: each loops MUTED once 45% in view (pauses out of view, hidden tabs, reduced motion
 * = poster only); "Watch with sound" restarts it from 0:00 IN THE SAME FRAME with controls
 * (his ruling: no lightbox). Only one film plays with sound at a time. Prerender-safe: the
 * <video> ships with poster + preload="none"; nothing plays until a real intersection.
 *
 * Analytics: cta_click{hire_ai_employee, location hero|after_films|close} ·
 * cta_click{meetings_film_play, film} · video_progress_25/50/75/100{video: meetings-<film>}.
 */
const PAGE = 'ai-employees-meetings';
const HIRE_PATH = '/ai_employee';
let liveVideo = null; // the one film currently playing with sound
// "Four ways to talk to it" tiles and the phone graphics (Nitish, Sep 30 2026: the page sells calls too).
const WAY_ICONS = { chat: MessageSquare, meet: Video, phone: Phone, calls: PhoneOutgoing };
const WAVE = [10, 22, 34, 18, 28, 40, 24, 14, 30, 20, 12, 26, 16];

const Mark = ({ yes }) => (
  <span className={yes ? 'mtg-y' : 'mtg-n'} role="img" aria-label={yes ? 'Yes' : 'Not in their docs'}>
    {yes ? <Check size={13} strokeWidth={2.75} aria-hidden="true" /> : <X size={13} strokeWidth={2.75} aria-hidden="true" />}
  </span>
);

const Film = ({ film, onPlay }) => {
  const wrapRef = useRef(null);
  const vRef = useRef(null);
  const liveRef = useRef(false);
  const milestones = useRef(new Set());
  const [live, setLive] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    const v = vRef.current;
    if (!el || !v || typeof IntersectionObserver === 'undefined') return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let inView = false;
    const sync = () => {
      if (liveRef.current) return;
      if (inView && document.visibilityState === 'visible') {
        v.muted = true;
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { inView = e.isIntersecting && e.intersectionRatio >= 0.45; });
      sync();
    }, { threshold: [0, 0.45] });
    io.observe(el);
    document.addEventListener('visibilitychange', sync);
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  const play = () => {
    const v = vRef.current;
    if (!v) return;
    if (liveVideo && liveVideo !== v) liveVideo.pause();
    liveVideo = v;
    liveRef.current = true;
    setLive(true);
    v.loop = false;
    v.muted = false;
    v.controls = true;
    v.currentTime = 0;
    // Film 01 has its captions burned into the picture: never double them with the VTT.
    if (!film.burned && v.textTracks && v.textTracks[0]) v.textTracks[0].mode = 'showing';
    v.play().catch(() => {});
    onPlay(film);
  };

  const onTime = () => {
    const v = vRef.current;
    if (!liveRef.current || !v || !v.duration) return;
    const pct = (v.currentTime / v.duration) * 100;
    [25, 50, 75, 99].forEach((m) => {
      if (pct >= m && !milestones.current.has(m)) {
        milestones.current.add(m);
        trackEvent(`video_progress_${m === 99 ? 100 : m}`, { video: `meetings-${film.key}`, page: PAGE, location: `film_${film.n}` });
      }
    });
  };

  return (
    <div className={`mtg-film${live ? ' is-live' : ''}`} ref={wrapRef}>
      <video
        ref={vRef}
        muted
        loop
        playsInline
        preload="none"
        poster={film.poster}
        onTimeUpdate={onTime}
      >
        <source src={film.src} type="video/mp4" />
        <track kind="captions" srcLang="en" label="English" src={film.vtt} />
      </video>
      <button type="button" className="mtg-play" onClick={play} aria-label={`Play "${film.title}" with sound`}>
        <span className="mtg-play__i" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><path d="M8 5.14v13.72c0 .8.87 1.3 1.56.89l11.02-6.86a1.05 1.05 0 0 0 0-1.78L9.56 4.25A1.05 1.05 0 0 0 8 5.14Z" /></svg>
        </span>
        <span>Watch with sound · {film.duration}</span>
      </button>
    </div>
  );
};

// Dotted curve joining film i-1 to film i (they alternate sides; centres at 42% / 58% of the row).
const Curve = ({ i }) => (
  <li className="mtg-gap" aria-hidden="true">
    <svg className="mtg-curve" viewBox="0 0 1000 100" preserveAspectRatio="none">
      <path d={i % 2 === 1 ? 'M420,0 C420,55 580,45 580,100' : 'M580,0 C580,55 420,45 420,100'} vectorEffect="non-scaling-stroke" />
    </svg>
  </li>
);

const MeetingsLandingPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();
  const [openFaq, setOpenFaq] = useState(-1);
  const m = MEETINGS;
  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}${m.path}`;
  const ogImage = `${SEO_DEFAULTS.url}${m.ogImage}`;
  const cmp = m.compare;

  const handleHire = (loc) => {
    trackEvent('cta_click', { cta_name: 'hire_ai_employee', page: PAGE, location: loc });
    if (!currentUser) {
      saveIntendedUrl(HIRE_PATH);
      navigate('/signup');
      return;
    }
    navigate(HIRE_PATH);
  };
  const onFilmPlay = (f) => trackEvent('cta_click', { cta_name: 'meetings_film_play', film: f.key, page: PAGE, location: `film_${f.n}` });

  const videoSchema = (f) => ({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: f.schemaName,
    description: f.description,
    thumbnailUrl: [`${SEO_DEFAULTS.url}${f.poster}`],
    uploadDate: f.uploadDate,
    duration: f.iso,
    contentUrl: `${SEO_DEFAULTS.url}${f.src}`,
    embedUrl: canonicalUrl,
    isFamilyFriendly: true,
    inLanguage: 'en',
    transcript: f.transcript.map(([w, s]) => (w ? `${w}: ${s}` : s)).join(' '),
    publisher: { '@type': 'Organization', name: 'CellCog', logo: { '@type': 'ImageObject', url: `${SEO_DEFAULTS.url}/logo512.png` } },
  });

  return (
    <>
      <Helmet>
        <title>{m.seo.title}</title>
        <meta name="description" content={m.seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={m.seo.title} />
        <meta property="og:description" content={m.seo.description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content="An AI employee on a client call" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={m.seo.title} />
        <meta name="twitter:description" content={m.seo.description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateFAQSchema(m.faq.map((f) => ({ question: f.q, answer: f.a })))} />
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Home', url: `${SEO_DEFAULTS.url}/` },
          { name: 'AI Employees', url: `${SEO_DEFAULTS.url}/ai-employees` },
          { name: 'Meetings', url: canonicalUrl },
        ])}
      />
      {m.films.map((f) => <JsonLd key={f.key} data={videoSchema(f)} />)}

      <div className={`aie-landing mtg ${dark ? 'dark' : ''}`}>
        {/* 1. Hero: short, centred, the films start right under it (his "less white space") */}
        <section className="mtg-hero">
          <nav className="mtg-crumb" aria-label="Breadcrumb">
            <Link to="/ai-employees">AI Employees</Link>
            <span aria-hidden="true">/</span>
            <span>Meetings</span>
          </nav>
          <span className="aie-eyebrow">{m.hero.eyebrow}</span>
          <h1 className="mtg-h1 aie-display">
            {(() => {
              const ACC = m.hero.accent || 'in your meetings.';
              const i = m.hero.h1.indexOf(ACC);
              return i < 0 ? m.hero.h1 : (<>{m.hero.h1.slice(0, i)}<span className="mtg-h1__accent">{ACC}</span></>);
            })()}
          </h1>
          <p className="mtg-sub">{m.hero.sub}</p>
          <div className="mtg-ctas">
            <button type="button" className="aie-btn aie-btn-primary" data-msc-suppress onClick={() => handleHire('hero')}>
              Get started <ArrowRightOutlined />
            </button>
          </div>
          <p className="mtg-support">Try it free, no credit card needed</p>
        </section>

        {/* 2. Alex's story: three films on a curved timeline, no dates (his ruling) */}
        <section className="mtg-films" id="mtg-films" aria-label="Alex's story in three films">
          <ol className="mtg-tl">
            {m.films.map((f, i) => (
              <React.Fragment key={f.key}>
                {i > 0 && <Curve i={i} />}
                <li className={`mtg-beat ${i % 2 ? 'is-right' : 'is-left'}`}>
                  <div className="mtg-head">
                    <span className="mtg-dot" aria-hidden="true" />
                    <span className="mtg-num" aria-label={`Step ${f.n}: ${f.tag}`}>{f.n}</span>
                    <h2 className="mtg-t aie-display">{f.title}</h2>
                    <span className="mtg-line">{f.line}</span>
                  </div>
                  <Film film={f} onPlay={onFilmPlay} />
                  <details className="mtg-tx">
                    <summary>Transcript</summary>
                    <div className="mtg-tx__body">
                      {f.transcript.map(([w, s], j) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <p key={j}>{w && <b>{w}</b>} {s}</p>
                      ))}
                    </div>
                  </details>
                </li>
              </React.Fragment>
            ))}
          </ol>
          <div className="mtg-after">
            <p>{m.afterFilms}</p>
            <button type="button" className="aie-btn aie-btn-primary" data-msc-suppress onClick={() => handleHire('after_films')}>
              Get started <ArrowRightOutlined />
            </button>
          </div>
        </section>

        {/* 2b. Four ways to talk to it (Nitish, Sep 30 2026): after the films so the Nora video stays up top */}
        <section className="aie-section mtg-ways">
          <div className="aie-section-head">
            <span className="aie-eyebrow">{m.ways.eyebrow}</span>
            <h2 className="aie-section-title aie-display">{m.ways.title}</h2>
          </div>
          <div className="mtg-ways__grid">
            {m.ways.items.map((w) => {
              const Icon = WAY_ICONS[w.key] || MessageSquare;
              return (
                <div className="mtg-way" key={w.key}>
                  <span className="mtg-way__ic" aria-hidden="true"><Icon size={18} strokeWidth={2} /></span>
                  <h3>{w.title}</h3>
                  <p>{w.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* 3. How it works: titles only, beside the REAL wall Meet frame */}
        <section className="aie-section mtg-how">
          <div className="mtg-how__grid">
            <div>
              <span className="aie-eyebrow">{m.steps.eyebrow}</span>
              <h2 className="aie-section-title aie-display">{m.steps.title}</h2>
              <ol className="mtg-steps">
                {m.steps.items.map((s, i) => (
                  <li key={s}><span className="aie-steps__n">0{i + 1}</span><h3>{s}</h3></li>
                ))}
              </ol>
            </div>
            <div className="mtg-how__frame"><WallFrame cls="wm-meet" /></div>
          </div>
        </section>

        {/* 3b. Its own phone number (Nitish, Sep 30 2026): graphics, not text; no digits, no invented names */}
        <section className="aie-section mtg-phone">
          <div className="aie-section-head">
            <span className="aie-eyebrow">{m.phone.eyebrow}</span>
            <h2 className="aie-section-title aie-display">{m.phone.title}</h2>
            <p className="mtg-phone__lead">{m.phone.lead}</p>
          </div>
          <div className="mtg-phones">
            {m.phone.phones.map((p) => (
              <figure className="mtg-ph" key={p.key}>
                <span className="mtg-sr">{p.alt}</span>
                <div className="mtg-dev" aria-hidden="true">
                  <div className="mtg-scr">
                    <span className="mtg-scr__st">{p.status}</span>
                    <span className={`mtg-scr__av mtg-scr__av--${p.key}`}>
                      {p.initials || <User size={30} strokeWidth={1.75} />}
                    </span>
                    <span className="mtg-scr__nm">{p.name}</span>
                    <span className="mtg-scr__sub">{p.sub}</span>
                    <span className="mtg-scr__pill">{p.pill}</span>
                    <span className="mtg-scr__wave">
                      {WAVE.map((h, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <i key={i} style={{ height: h }} />
                      ))}
                    </span>
                    <span className="mtg-scr__end"><Phone size={22} strokeWidth={2} /></span>
                  </div>
                </div>
                <figcaption>{p.caption}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* 4. Trust: three short cards */}
        <section className="aie-section mtg-trust">
          <h2 className="aie-section-title aie-display">{m.trust.title}</h2>
          <div className="mtg-trust__rows">
            {m.trust.rows.map((r) => (
              <div className="mtg-trust__row" key={r.title}>
                <Mark yes />
                <div><h3>{r.title}</h3><p>{r.body}</p></div>
              </div>
            ))}
          </div>
        </section>

        {/* 5. Comparison: logos, not names (his ruling); cells = each company's own docs */}
        <section className="aie-section mtg-cmp">
          <div className="aie-section-head">
            <span className="aie-eyebrow">{cmp.eyebrow}</span>
            <h2 className="aie-section-title aie-display">{cmp.title[0]}<br />{cmp.title[1]}</h2>
          </div>
          <div className="mtg-cmp__wrap">
            <table className="mtg-cmp__t">
              <caption className="mtg-sr">AI employees in meetings, compared with Spinnable, Grok Bot, Lindy, Sintra and Viktor</caption>
              <thead>
                <tr>
                  <th aria-label="Capability" />
                  {cmp.cols.map((c, ci) => (
                    <th scope="col" key={c.key} className={ci === 0 ? 'is-us' : undefined}>
                      <span className="mtg-logo"><img src={c.logo} alt="" width="40" height="40" loading="lazy" /></span>
                      <span className="mtg-name">{c.name}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cmp.rows.map((r) => (
                  <tr key={r.label}>
                    <th scope="row">{r.label}</th>
                    {r.values.map((v, ci) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={ci} className={ci === 0 ? 'is-us' : undefined}><Mark yes={!!v} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mtg-cmp__cards">
            {cmp.cols.map((c, ci) => (
              <div className={`mtg-card${ci === 0 ? ' is-us' : ''}`} key={c.key}>
                <div className="mtg-card__h">
                  <span className="mtg-logo"><img src={c.logo} alt="" width="28" height="28" loading="lazy" /></span>
                  <span className="mtg-name">{c.name}</span>
                </div>
                <ul>
                  {cmp.rows.map((r) => (
                    <li key={r.label}><Mark yes={!!r.values[ci]} /><span>{r.label}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mtg-cmp__note">
            {cmp.note} Sources:{' '}
            {cmp.cols.filter((c) => c.source).map((c, i, arr) => (
              <React.Fragment key={c.key}>
                <a href={c.source} target="_blank" rel="nofollow noopener noreferrer">{c.name}</a>
                {(c.more || []).map((x) => (
                  <React.Fragment key={x.source}>
                    {' ('}<a href={x.source} target="_blank" rel="nofollow noopener noreferrer">{x.label}</a>{')'}
                  </React.Fragment>
                ))}
                {i < arr.length - 1 ? ', ' : '.'}
              </React.Fragment>
            ))}
          </p>
        </section>

        {/* 6. FAQ: the visible list IS the FAQPage JSON-LD */}
        <section className="aie-faq aie-section" aria-labelledby="mtg-faq-title">
          <div className="aie-section-head">
            <span className="aie-eyebrow">Questions</span>
            <h2 className="aie-section-title aie-display" id="mtg-faq-title">{m.faqTitle}</h2>
          </div>
          <div className="aie-faq__list">
            {m.faq.map((f, i) => {
              const open = openFaq === i;
              return (
                <div className={`aie-faq__item${open ? ' is-open' : ''}`} key={f.q}>
                  <button type="button" className="aie-faq__q" aria-expanded={open} onClick={() => setOpenFaq(open ? -1 : i)}>
                    <span>{f.q}</span>
                    <PlusOutlined className="aie-faq__icon" />
                  </button>
                  <div className="aie-faq__a-wrap" role="region">
                    <p className="aie-faq__a">{f.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <AIEClose ctaLabel="Get started" onPrimary={() => handleHire('close')} title={m.close.title} sub={m.close.sub} />
      </div>
    </>
  );
};

export default MeetingsLandingPage;
